/**
 * Hook para gerenciar configurações do sistema (branding, etc.)
 * Permite buscar e atualizar configurações globais armazenadas na tabela system_settings
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { logger } from '@/lib/logger';

// Tipagem para as configurações de branding
export interface BrandingSettings {
  logo_url: string | null;
  icon_url: string | null;
  login_logo_url: string | null;
  app_name: string;
}

// Valores padrão caso não haja configuração
const DEFAULT_BRANDING: BrandingSettings = {
  logo_url: null,
  icon_url: null,
  login_logo_url: null,
  app_name: 'UaizyChat'
};

/**
 * Hook para buscar e atualizar configurações de branding do sistema
 */
export function useSystemSettings() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Query para buscar configurações de branding
  const { data: branding, isLoading, error, refetch } = useQuery({
    queryKey: ['system-settings', 'branding'],
    queryFn: async (): Promise<BrandingSettings> => {
      logger.debug('[useSystemSettings]', 'Buscando configurações de branding...');
      
      // Server-side: substitui SELECT direto em system_settings por RPC
      const { data, error } = await supabase.rpc('get_branding_settings');

      if (error) {
        logger.error('[useSystemSettings]', 'Erro ao buscar branding: ' + error.message);
        return DEFAULT_BRANDING;
      }

      const row = Array.isArray(data) ? data[0] : null;
      const settings: BrandingSettings = {
        logo_url: row?.logo_url ?? null,
        icon_url: row?.icon_url ?? null,
        login_logo_url: row?.login_logo_url ?? null,
        app_name: row?.app_name || 'UaizyChat',
      };

      logger.debug('[useSystemSettings]', 'Branding carregado: ' + JSON.stringify(settings));
      return settings;
    },
    staleTime: 5 * 60 * 1000, // Cache por 5 minutos
  });

  // Mutation para atualizar configurações de branding
  const updateBrandingMutation = useMutation({
    mutationFn: async (newSettings: Partial<BrandingSettings>) => {
      logger.info('[useSystemSettings]', 'Atualizando branding: ' + JSON.stringify(newSettings));
      
      const currentSettings = branding || DEFAULT_BRANDING;
      const updatedSettings = { ...currentSettings, ...newSettings };
      
      const { error } = await supabase
        .from('system_settings')
        .update({ value: updatedSettings })
        .eq('key', 'branding');
      
      if (error) {
        logger.error('[useSystemSettings]', 'Erro ao atualizar branding: ' + error.message);
        throw error;
      }
      
      return updatedSettings;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['system-settings', 'branding'], data);
      toast({
        title: 'Configurações salvas',
        description: 'As configurações de branding foram atualizadas com sucesso.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao salvar',
        description: error.message || 'Não foi possível salvar as configurações.',
        variant: 'destructive',
      });
    },
  });

  // Função para fazer upload de imagem para o storage
  // Função para fazer upload de imagem para o storage (logo, icon ou login_logo)
  const uploadBrandingImage = async (
    file: File, 
    type: 'logo' | 'icon' | 'login_logo'
  ): Promise<string> => {
    const fileExt = file.name.split('.').pop()?.toLowerCase() || 'png';
    const filePath = `system/${type}.${fileExt}`;
    
    logger.info('[useSystemSettings]', `Fazendo upload de ${type}: ${filePath}`);
    
    const filesToDelete = type === 'logo' 
      ? ['system/logo.png', 'system/logo.jpg', 'system/logo.svg', 'system/logo.webp']
      : type === 'icon'
      ? ['system/icon.png', 'system/icon.jpg', 'system/icon.svg', 'system/icon.webp']
      : ['system/login_logo.png', 'system/login_logo.jpg', 'system/login_logo.svg', 'system/login_logo.webp'];
    
    await supabase.storage.from('anexos').remove(filesToDelete);
    
    const { error: uploadError } = await supabase.storage
      .from('anexos')
      .upload(filePath, file, { upsert: true });
    
    if (uploadError) {
      logger.error('[useSystemSettings]', `Erro no upload de ${type}: ${uploadError.message}`);
      throw uploadError;
    }
    
    const { data: urlData } = supabase.storage
      .from('anexos')
      .getPublicUrl(filePath);
    
    const publicUrl = `${urlData.publicUrl}?t=${Date.now()}`;
    logger.info('[useSystemSettings]', `URL pública do ${type}: ${publicUrl}`);
    
    return publicUrl;
  };

  return {
    branding: branding || DEFAULT_BRANDING,
    isLoading,
    error,
    refetch,
    updateBranding: updateBrandingMutation.mutateAsync,
    isUpdating: updateBrandingMutation.isPending,
    uploadBrandingImage,
  };
}
